import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { leagueAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../components/Alert';
import './LeagueList.css';

const libraries = ['places', 'marker'];
const DEFAULT_MAP_CENTER = { lat: 51.5074, lng: -0.1278 }; // London

const LeagueList = () => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const { user, refreshUser } = useAuth();
  const { showAlert } = useAlert();
  const [leagues, setLeagues] = useState([]);
  const [leagueTypeFilter, setLeagueTypeFilter] = useState('all'); // 'all', 'super', 'individual'
  const [clubFilter, setClubFilter] = useState('all'); // 'all', 'public', 'club'
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'
  const [dateFilter, setDateFilter] = useState('all'); // 'startingSoon', 'ongoing', 'completed', 'all'
  const [sportFilter, setSportFilter] = useState('Tennis (singles)');
  const [sportIcon, setSportIcon] = useState('🎾');
  const [minLevelFilter, setMinLevelFilter] = useState('');
  const [maxLevelFilter, setMaxLevelFilter] = useState('');
  const [distanceFilter, setDistanceFilter] = useState(5); // in km, list view only
  const [userPosition, setUserPosition] = useState(null);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  const [locationChecked, setLocationChecked] = useState(false); // Track if we've checked for location
  const [isFirstMapLoad, setIsFirstMapLoad] = useState(true); // Track if this is the first time loading map view
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_MAP_CENTER);
  const [initialMapCenter, setInitialMapCenter] = useState(DEFAULT_MAP_CENTER); // Store initial center, don't update after
  const mapInitializedRef = useRef(false); // Track if map has been initialized (ref doesn't trigger re-renders)
  const stableCenterRef = useRef(DEFAULT_MAP_CENTER); // Store stable center in ref - never changes after first set
  const defaultCenterSetRef = useRef(false); // Track if defaultCenter has been passed to prevent re-setting on re-mounts
  const fitBoundsPendingRef = useRef(false); // Track if we're waiting to fit bounds
  const [mapRef, setMapRef] = useState(null);
  const markersRef = useRef([]); // Store marker instances
  const markerClustererRef = useRef(null); // Store clusterer instance
  const [mapBounds, setMapBounds] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapZoom, setMapZoom] = useState(null); // Track current zoom level
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [overlappingLeagues, setOverlappingLeagues] = useState(null); // For markers with multiple leagues
  const [markersLoading, setMarkersLoading] = useState(false); // Track marker clustering state
  const [queryCenter, setQueryCenter] = useState(null); // Center point used for geohash query
  const [lastMapQueryCenter, setLastMapQueryCenter] = useState(null); // Remember last map view query center
  const [mapHasMoved, setMapHasMoved] = useState(false); // Track if map moved since last search
  const [pendingMapCenter, setPendingMapCenter] = useState(null); // Map center waiting for search
  const [showFilters, setShowFilters] = useState(false); // Track if filters are visible on mobile
  
  // Memoize map center with stable reference
  const mapCenterValue = useMemo(() => {
    let center;
    if (!isFirstMapLoad && queryCenter) {
      center = queryCenter;
    } else {
      center = initialMapCenter || DEFAULT_MAP_CENTER;
    }
    
    // Return stable reference if values haven't changed
    if (stableCenterRef.current && 
        Math.abs(stableCenterRef.current.lat - center.lat) < 0.000001 &&
        Math.abs(stableCenterRef.current.lng - center.lng) < 0.000001) {
      return stableCenterRef.current;
    }
    
    // Update stable reference with new value
    stableCenterRef.current = center;
    return center;
  }, [initialMapCenter, queryCenter, isFirstMapLoad]);
  
  // Calculate search radius based on zoom level
  const calculateRadiusFromZoom = (zoom) => {
    if (!zoom) return 50; // Default fallback
    
    // Map zoom levels to appropriate search radius in km
    if (zoom <= 8) return 100;
    if (zoom <= 10) return 50;
    if (zoom <= 12) return 25;
    if (zoom <= 14) return 10;
    return 5;
  };

  const getMatchFormatDisplay = (format) => {
    const formats = {
      'best-of-3-sets-to-6': 'Best of 3 Sets (to 6)',
      'best-of-3-sets-to-4': 'Best of 3 Sets (to 4)',
      'best-of-3-deciding-tiebreak-sets-to-6': 'Best of 3 (3rd set tiebreak)',
      'best-of-3-deciding-tiebreak-sets-to-4': 'Best of 3 (3rd set tiebreak, sets to 4)',
      'single-set': 'Single Set',
      'squash-best-of-5-games-to-11': 'Best of 5 Games (First to 11, win by 2)',
      'squash-best-of-3-games-to-11': 'Best of 3 Games (First to 11, win by 2)',
    };
    return formats[format] || format;
  };

  useEffect(() => {
    if (user?.defaultSport) {
      setSportFilter(user.defaultSport);
    } else {
      // Default to Tennis (singles) if user has no default sport
      setSportFilter('Tennis (singles)');
    }
  }, [user]);

  useEffect(() => {
    const sportIcons = {
      'Tennis (singles)': '🎾',
      'Tennis (doubles)': '🎾',
      Padel: '🏓',
      Squash: '🏸',
      Football: '⚽',
      Basketball: '🏀',
    };

    if (sportFilter && sportIcons[sportFilter]) {
      setSportIcon(sportIcons[sportFilter]);
    } else {
      setSportIcon('');
    }
  }, [sportFilter]);

  // Track previous viewMode to detect actual switches
  const prevViewModeRef = useRef(viewMode);

  useEffect(() => {
    const prevViewMode = prevViewModeRef.current;
    prevViewModeRef.current = viewMode;
    
    // Only reset map state when SWITCHING TO map view, not when already in map view
    if (viewMode === 'map' && prevViewMode !== 'map') {
      setSelectedLeague(null);
      setMapBounds(null);
      setMapReady(false);
      setMapRef(null);
      setMapZoom(null); // Reset zoom when switching to map view
      setMapHasMoved(false);
      fitBoundsPendingRef.current = false; // Reset fit bounds pending flag
      mapInitializedRef.current = false; // Reset so map can center on initial load
      defaultCenterSetRef.current = false; // Reset so defaultCenter can be set again
      
      // Restore last map query center when switching back to map view (if not first load)
      if (!isFirstMapLoad && lastMapQueryCenter) {
        setQueryCenter(lastMapQueryCenter);
      }
    } else if (viewMode !== 'map') {
      setSelectedLeague(null);
    }
  }, [viewMode, lastMapQueryCenter, isFirstMapLoad]);

  // Resize map when switching to map view to fix grey map issue
  useEffect(() => {
    if (viewMode === 'map' && isLoaded && mapRef) {
      // Trigger resize after DOM updates to ensure container has dimensions
      const resizeTimeout = setTimeout(() => {
        if (mapRef && window.google?.maps?.event) {
          window.google.maps.event.trigger(mapRef, 'resize');
          // Also update bounds after resize
          const bounds = mapRef.getBounds();
          if (bounds) {
            setMapBounds(bounds);
          }
        }
      }, 200);
      return () => clearTimeout(resizeTimeout);
    }
  }, [viewMode, isLoaded, mapRef]);

  useEffect(() => {
    if (!selectedLeague) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedLeague(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedLeague]);

  // Get user's location on mount - set initial map center only once
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setMapCenter(pos);
          // Only set initialMapCenter if it's still the default (first time)
          setInitialMapCenter(prev => {
            const newValue = prev === DEFAULT_MAP_CENTER ? pos : prev;
            // Update ref when we set initial center for the first time
            if (prev === DEFAULT_MAP_CENTER) {
              stableCenterRef.current = pos;
            }
            return newValue;
          });
          setUserPosition(pos);
          setLocationPermissionDenied(false);
          setLocationChecked(true); // Mark that we've obtained location
        },
        (err) => {
          console.warn('Geolocation error:', err.message);
          setMapCenter(DEFAULT_MAP_CENTER);
          // initialMapCenter already defaults to DEFAULT_MAP_CENTER, no need to set
          setLocationPermissionDenied(true);
          setLocationChecked(true); // Mark that we've checked (but denied)
        }
      );
    } else {
      // initialMapCenter already defaults to DEFAULT_MAP_CENTER, no need to set
      setLocationPermissionDenied(true);
      setLocationChecked(true); // Mark that we've checked (but not supported)
    }
  }, [user]);

  // Load leagues when query center changes (for list view only - map view uses manual search button)
  useEffect(() => {
    if (viewMode === 'map' && mapCenter && !queryCenter && locationChecked && isFirstMapLoad) {
      // For map view, set initial query center ONLY after we've checked location AND it's the first load
      // This ensures we use user's actual location on first load, not the default London location
      // After first load, we don't reset queryCenter when switching back to map view
      setQueryCenter(mapCenter);
      setLastMapQueryCenter(mapCenter); // Save this as the last map query center
      setMapHasMoved(false);
      setIsFirstMapLoad(false); // Mark that we've done the first load
    } else if (viewMode === 'list' && userPosition) {
      // For list view, load leagues around user's position
      setQueryCenter(userPosition);
    }
  }, [viewMode, mapCenter, userPosition, queryCenter, locationChecked, isFirstMapLoad]);

  // Load leagues when queryCenter changes
  useEffect(() => {
    if (queryCenter) {
      loadLeagues();
    } else if (viewMode === 'list' && !userPosition && locationPermissionDenied) {
      // In list view without location, don't load - show warning instead
      setLoading(false);
      setLeagues([]);
    }
  }, [queryCenter]);


  // Reload leagues when switching to list view (use user position)
  useEffect(() => {
    if (viewMode === 'list') {
      if (userPosition) {
        setQueryCenter(userPosition);
      } else if (locationPermissionDenied) {
        setLoading(false);
        setLeagues([]);
      }
    }
  }, [viewMode, userPosition, locationPermissionDenied]);

  const loadLeagues = async (paginationToken = null, radiusKm = null) => {
    // Don't load if we don't have a query center
    if (!queryCenter) {
      setLoading(false);
      return;
    }

    // Use provided radius or calculate based on view mode
    // For map view, use zoom level to determine radius; for list view, use distance filter
    const searchRadius = radiusKm ?? (viewMode === 'list' ? distanceFilter : calculateRadiusFromZoom(mapZoom));

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/4b0cfd8f-20a7-423f-bb09-3fd441b6c229',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LeagueList.js:275',message:'loadLeagues called',data:{queryCenter:queryCenter,searchRadius:searchRadius,mapZoom:mapZoom,isPagination:!!paginationToken},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H5'})}).catch(()=>{});
    // #endregion

    try {
      if (paginationToken) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // Don't clear leagues here - keep existing markers visible while loading
        // setLeagues([]); // Removed to prevent marker destruction
        setNextToken(null);
        setHasMore(false);
      }

      // Use geohash-based query with lat/lng and radius
      // Pass server-side filters (sport, date, level, league type, club status)
      // Also pass map bounds if available for more accurate area coverage
      let boundsObj = null;
      if (viewMode === 'map' && mapBounds) {
        const ne = mapBounds.getNorthEast();
        const sw = mapBounds.getSouthWest();
        boundsObj = {
          neLat: ne.lat(),
          neLng: ne.lng(),
          swLat: sw.lat(),
          swLng: sw.lng(),
        };
      }
      
      const response = await leagueAPI.getAllLeagues(
        queryCenter.lat,
        queryCenter.lng,
        searchRadius,
        25, // Page size (reduced from 100 for better performance)
        paginationToken,
        {
          sport: sportFilter,
          dateFilter: dateFilter,
          minLevel: minLevelFilter,
          maxLevel: maxLevelFilter,
          leagueTypeFilter: leagueTypeFilter,
          clubFilter: clubFilter,
        },
        boundsObj // Pass map bounds for accurate geohash coverage
      );
      
      // Handle paginated response structure
      const leaguesData = response.data.leagues || response.data;
      console.log('leaguesData', leaguesData);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/4b0cfd8f-20a7-423f-bb09-3fd441b6c229',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LeagueList.js:299',message:'Leagues loaded from API',data:{count:leaguesData.length,hasCoords:leaguesData.filter(l=>l.latitude&&l.longitude).length,firstLeague:leaguesData[0]},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      
      if (paginationToken) {
        setLeagues(prev => [...prev, ...leaguesData]);
      } else {
        setLeagues(leaguesData);
        // Note: We no longer re-center the map on loaded leagues
        // This was causing the map to center on default (London) leagues instead of user location
      }

      // Update pagination state
      setHasMore(response.data.hasMore || false);
      setNextToken(response.data.nextToken || null);
      
      
      // Filter will be applied in useEffect
    } catch (err) {
      console.error('Failed to load leagues:', err);
      if (err.response?.status === 400 && err.response?.data?.error === 'Location required') {
        setError('Please enable location services to view nearby leagues');
      } else {
        setError('Failed to load leagues');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreLeagues = () => {
    if (!loadingMore && hasMore && nextToken && queryCenter) {
      loadLeagues(nextToken, viewMode === 'list' ? distanceFilter : calculateRadiusFromZoom(mapZoom));
    }
  };

  // Reload leagues when map center changes significantly (for map view)
  const reloadForMapCenter = useCallback(() => {
    if (viewMode === 'map' && mapCenter) {
      setQueryCenter(mapCenter);
    }
  }, [viewMode, mapCenter]);

  // Reload leagues when distance filter changes in list view
  useEffect(() => {
    if (viewMode === 'list' && queryCenter && userPosition) {
      loadLeagues(null, distanceFilter);
    }
  }, [distanceFilter]);

  // Reload leagues when server-side filters change (sport, date, level, league type, club status)
  useEffect(() => {
    if (queryCenter) {
      // Reset pagination when filters change
      // Use distance filter for list view, zoom-based radius for map view
      const radius = viewMode === 'list' ? distanceFilter : calculateRadiusFromZoom(mapZoom);
      loadLeagues(null, radius);
    }
  }, [sportFilter, dateFilter, minLevelFilter, maxLevelFilter, leagueTypeFilter, clubFilter]);


  // Memoize filteredLeagues to prevent unnecessary re-renders of marker effect
  const filteredLeagues = useMemo(() => {
    let filtered = leagues;

    // Note: All filtering (sport, date, level, league type, club status, distance) 
    // is now done server-side. We show all loaded leagues without additional filtering.
    // The backend already filters by radius around the search center.

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/4b0cfd8f-20a7-423f-bb09-3fd441b6c229',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LeagueList.js:430',message:'filterLeagues memoized',data:{totalLeagues:leagues.length,filteredCount:filtered.length},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H2'})}).catch(()=>{});
    // #endregion

    return filtered;
  }, [leagues]); // Only depend on leagues, not viewMode

  const onMapLoad = useCallback((map) => {
    setMapRef(map);
    
    // If markers exist and map remounted, recreate clusterer with new map
    if (markersRef.current.length > 0) {
      // Clear old clusterer if it exists
      if (markerClustererRef.current) {
        markerClustererRef.current.clearMarkers();
        markerClustererRef.current.setMap(null);
      }
      
      // Create new clusterer with existing markers on new map
      markerClustererRef.current = new MarkerClusterer({
        map: map,
        markers: markersRef.current,
      });
    }
    
    // Ensure map has a valid center - if it doesn't, set it to the current center value
    const currentCenter = map.getCenter();
    if (!currentCenter || (currentCenter.lat() === 0 && currentCenter.lng() === 0)) {
      // Map doesn't have a valid center, set it explicitly
      const centerToUse = mapCenterValue || initialMapCenter || DEFAULT_MAP_CENTER;
      map.setCenter(centerToUse);
    }
    
    // Get initial zoom level
    const zoom = map.getZoom();
    if (zoom) {
      setMapZoom(zoom);
    }
    
    // Set initial bounds
    const bounds = map.getBounds();
    if (bounds) {
      setMapBounds(bounds);
    }
    // Mark map as initialized - center prop will now use stable initialMapCenter
    const wasInitialized = mapInitializedRef.current;
    mapInitializedRef.current = true;
    // Resize map to fix grey map issue - trigger after DOM update
    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (window.google?.maps?.event) {
          window.google.maps.event.trigger(map, 'resize');
          // Ensure center is still valid after resize
          const centerAfterResize = map.getCenter();
          if (!centerAfterResize || (centerAfterResize.lat() === 0 && centerAfterResize.lng() === 0)) {
            const centerToUse = mapCenterValue || initialMapCenter || DEFAULT_MAP_CENTER;
            map.setCenter(centerToUse);
          }
          // Update bounds after resize
          const updatedBounds = map.getBounds();
          if (updatedBounds) {
            setMapBounds(updatedBounds);
          }
        }
        setMapReady(true);
      }, 100);
    });
  }, [initialMapCenter, mapCenterValue]);

  const onMapIdle = useCallback(() => {
    if (fitBoundsPendingRef.current && mapRef) {
      const bounds = mapRef.getBounds();
      if (bounds) {
        setMapBounds(bounds);
      }
    }
  }, [mapRef]);

  const onBoundsChanged = useCallback(() => {
    if (mapRef) {
      const bounds = mapRef.getBounds();
      if (bounds) {
        setMapBounds(bounds);
      }
      
      // Track zoom level changes
      const zoom = mapRef.getZoom();
      if (zoom) {
        setMapZoom(zoom);
      }
      
      // Track map center changes for manual search
      const center = mapRef.getCenter();
      if (center) {
        const newCenter = { lat: center.lat(), lng: center.lng() };
        setPendingMapCenter(newCenter);
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/4b0cfd8f-20a7-423f-bb09-3fd441b6c229',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LeagueList.js:519',message:'Map moved - pending center updated',data:{newCenter:newCenter,queryCenter:queryCenter,hasMoved:queryCenter&&(Math.abs(newCenter.lat-queryCenter.lat)>0.01||Math.abs(newCenter.lng-queryCenter.lng)>0.01)},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H5'})}).catch(()=>{});
        // #endregion
        // Check if moved significantly from last query center
        if (queryCenter && 
            (Math.abs(newCenter.lat - queryCenter.lat) > 0.01 || 
             Math.abs(newCenter.lng - queryCenter.lng) > 0.01)) {
          setMapHasMoved(true);
        }
      }
    }
  }, [mapRef, queryCenter, initialMapCenter]);

  // Handle manual search in map view
  const handleMapSearch = useCallback(() => {
    // Check if user is too zoomed out (require zoom 12+ for ~25km radius / 50x50km area)
    const currentZoom = mapRef?.getZoom();
    if (currentZoom && currentZoom < 12) {
      const estimatedRadius = calculateRadiusFromZoom(currentZoom);
      showAlert(
        `🔍 Please zoom in closer to search. Current view covers ~${estimatedRadius * 2}km radius. Zoom in to city level or closer for better results.`,
        'warning'
      );
      return;
    }

    if (pendingMapCenter) {
      setQueryCenter(pendingMapCenter);
      setLastMapQueryCenter(pendingMapCenter); // Save this as the last map query center
      setMapHasMoved(false);
      // Map stays at current location and zoom - just loads new markers
    }
  }, [pendingMapCenter, mapRef, showAlert]);

  // Group leagues by location (within ~10 meters tolerance)
  const groupLeaguesByLocation = useCallback((leagues) => {
    const LOCATION_TOLERANCE = 0.0001; // ~10 meters
    const groups = [];
    
    leagues.forEach(league => {
      // Find existing group at this location
      const existingGroup = groups.find(group => 
        Math.abs(group.lat - league.latitude) < LOCATION_TOLERANCE &&
        Math.abs(group.lng - league.longitude) < LOCATION_TOLERANCE
      );
      
      if (existingGroup) {
        existingGroup.leagues.push(league);
      } else {
        groups.push({
          lat: league.latitude,
          lng: league.longitude,
          leagues: [league]
        });
      }
    });
    
    return groups;
  }, []);

  // Update markers and clusterer when leagues or map changes
  // Using a ref to track current league IDs to avoid unnecessary updates
  const currentLeagueIdsRef = useRef([]);
  
  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/4b0cfd8f-20a7-423f-bb09-3fd441b6c229',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LeagueList.js:534',message:'Marker useEffect triggered',data:{mapRef:!!mapRef,mapReady:mapReady,viewMode:viewMode,hasGoogle:!!window.google,filteredLeaguesCount:filteredLeagues.length},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
    // #endregion
    
    if (!mapRef || !mapReady || viewMode !== 'map' || !window.google) return;

    // Get leagues with coordinates
    const leaguesWithCoords = filteredLeagues.filter(
      league => league.latitude && league.longitude
    );

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/4b0cfd8f-20a7-423f-bb09-3fd441b6c229',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LeagueList.js:555',message:'Leagues with coords filtered',data:{total:filteredLeagues.length,withCoords:leaguesWithCoords.length,sample:leaguesWithCoords.slice(0,2).map(l=>({id:l.leagueId,lat:l.latitude,lng:l.longitude}))},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
    // #endregion

    // Check if leagues have actually changed by comparing IDs
    const newLeagueIds = leaguesWithCoords.map(l => l.leagueId).sort().join(',');
    const currentLeagueIds = currentLeagueIdsRef.current.join(',');
    
    // Skip update if leagues haven't changed
    if (newLeagueIds === currentLeagueIds && markersRef.current.length > 0) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/4b0cfd8f-20a7-423f-bb09-3fd441b6c229',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LeagueList.js:570',message:'Skipping marker update - leagues unchanged',data:{leagueCount:leaguesWithCoords.length,markerCount:markersRef.current.length},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
      // #endregion
      return; // Leagues haven't changed, keep existing markers
    }

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/4b0cfd8f-20a7-423f-bb09-3fd441b6c229',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LeagueList.js:575',message:'Leagues changed - updating markers',data:{oldIds:currentLeagueIds,newIds:newLeagueIds},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
    // #endregion

    // Clear existing markers and clusterer
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
    }
    markersRef.current.forEach(marker => {
      marker.map = null;
    });
    markersRef.current = [];

    if (leaguesWithCoords.length === 0) {
      // No leagues to show - markers already cleared above
      return;
    }

    // Show loading state for large marker sets
    if (leaguesWithCoords.length > 50) {
      setMarkersLoading(true);
    }

    // Group leagues by location to handle overlapping markers
    const locationGroups = groupLeaguesByLocation(leaguesWithCoords);

    // Create new markers (one per location, even if multiple leagues at that location)
    const newMarkers = locationGroups.map(group => {
      const leaguesCount = group.leagues.length;
      const firstLeague = group.leagues[0];
      
      // Create marker title showing count if multiple leagues
      const title = leaguesCount > 1 
        ? `${leaguesCount} leagues at this location`
        : firstLeague.name;

      // Create custom marker content with blue color and count badge
      const markerContent = document.createElement('div');
      markerContent.style.position = 'relative';
      markerContent.style.cursor = 'pointer';
      
      // Create the pin using SVG with primary blue color
      const pin = document.createElement('div');
      pin.innerHTML = `
        <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.16 0 0 7.16 0 16c0 8.84 16 24 16 24s16-15.16 16-24c0-8.84-7.16-16-16-16z" 
                fill="#2563eb" 
                stroke="white" 
                stroke-width="2"/>
          <circle cx="16" cy="16" r="6" fill="white"/>
        </svg>
      `;
      pin.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))';
      markerContent.appendChild(pin);
      
      // Add count badge if multiple leagues
      if (leaguesCount > 1) {
        const badge = document.createElement('div');
        badge.textContent = leaguesCount;
        badge.style.position = 'absolute';
        badge.style.top = '-8px';
        badge.style.right = '-8px';
        badge.style.backgroundColor = '#dc2626';
        badge.style.color = 'white';
        badge.style.borderRadius = '50%';
        badge.style.width = '24px';
        badge.style.height = '24px';
        badge.style.display = 'flex';
        badge.style.alignItems = 'center';
        badge.style.justifyContent = 'center';
        badge.style.fontSize = '12px';
        badge.style.fontWeight = '700';
        badge.style.border = '2px solid white';
        badge.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
        markerContent.appendChild(badge);
      }

      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat: group.lat, lng: group.lng },
        title: title,
        content: markerContent,
        // Don't set map here - let MarkerClusterer manage it
      });

      // Add click listener - show list if multiple leagues, detail if single
      marker.addListener('click', () => {
        if (leaguesCount > 1) {
          setOverlappingLeagues(group.leagues);
        } else {
          setSelectedLeague(firstLeague);
        }
      });

      return marker;
    });

    markersRef.current = newMarkers;
    currentLeagueIdsRef.current = leaguesWithCoords.map(l => l.leagueId).sort();

    // Update or create MarkerClusterer (use setTimeout for large sets to avoid blocking)
    setTimeout(() => {
      if (markerClustererRef.current) {
        // Update existing clusterer with new markers
        markerClustererRef.current.clearMarkers();
        markerClustererRef.current.addMarkers(newMarkers);
      } else {
        // Create new clusterer with custom blue renderer
        markerClustererRef.current = new MarkerClusterer({
          map: mapRef,
          markers: newMarkers,
          renderer: {
            render: ({ count, position }) => {
              // Create custom cluster marker with primary blue color
              const clusterContent = document.createElement('div');
              clusterContent.style.backgroundColor = '#2563eb';
              clusterContent.style.color = 'white';
              clusterContent.style.borderRadius = '50%';
              clusterContent.style.width = '50px';
              clusterContent.style.height = '50px';
              clusterContent.style.display = 'flex';
              clusterContent.style.alignItems = 'center';
              clusterContent.style.justifyContent = 'center';
              clusterContent.style.fontSize = '16px';
              clusterContent.style.fontWeight = '700';
              clusterContent.style.border = '3px solid white';
              clusterContent.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
              clusterContent.style.cursor = 'pointer';
              clusterContent.textContent = count;
              
              return new window.google.maps.marker.AdvancedMarkerElement({
                position,
                content: clusterContent,
                zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
              });
            },
          },
        });
      }
      setMarkersLoading(false);
    }, 0);
    
    // #region agent log
    setTimeout(()=>{fetch('http://127.0.0.1:7242/ingest/4b0cfd8f-20a7-423f-bb09-3fd441b6c229',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LeagueList.js:628',message:'Markers status after 100ms',data:{markerCount:markersRef.current.length,stillOnMap:markersRef.current.filter(m=>m.map!==null).length},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H6'})}).catch(()=>{});},100);
    // #endregion

    // NO CLEANUP FUNCTION - markers persist until explicitly changed
  }, [mapRef, mapReady, filteredLeagues, viewMode, groupLeaguesByLocation]);

  // Memoize map props to prevent unnecessary remounts
  const mapContainerStyle = useMemo(() => ({ width: '100%', height: '500px' }), []);
  const mapOptions = useMemo(() => ({
    mapId: 'DEMO_MAP_ID',
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    gestureHandling: 'greedy',
  }), []);

  if (loading || (viewMode === 'map' && !locationChecked)) {
    return (
      <div className="page-container">
        <div className="loading">
          {!locationChecked ? 'Getting your location...' : 'Loading leagues...'}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container">
        {error && <div className="error-message">{error}</div>}

        {/* Filters */}
        <div className="card filters-card">
          <div className="filters-header">
            <div className="view-toggle">
              <div className="toggle-label">View</div>
              <div className="toggle-buttons">
                <button
                  type="button"
                  className={`toggle-button ${viewMode === 'map' ? 'active' : ''}`}
                  onClick={() => setViewMode('map')}
                >
                  Map
                </button>
                <button
                  type="button"
                  className={`toggle-button ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                >
                  List
                </button>
              </div>
            </div>
            
            <button
              type="button"
              className="btn btn-secondary btn-sm show-filters-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? '✕ Hide Filters' : '🔽 Show Filters'}
            </button>
          </div>
          
          <div className={`filters-grid ${showFilters ? 'filters-visible' : ''}`}>
            <div className="filter-group">
              <label className="form-label">Sport</label>
              <div className="sport-select-wrapper">
                <select
                  className="form-input"
                  value={sportFilter}
                  onChange={(e) => setSportFilter(e.target.value)}
                >
                  <option value="Tennis (singles)">🎾 Tennis (singles)</option>
                  <option value="Tennis (doubles)" disabled>🎾 Tennis (doubles) (Coming Soon)</option>
                  <option value="Padel" disabled>🏓 Padel (Coming Soon)</option>
                  <option value="Squash">🏸 Squash</option>
                  <option value="Football" disabled>⚽ Football (Coming Soon)</option>
                  <option value="Basketball" disabled>🏀 Basketball (Coming Soon)</option>
                </select>
              </div>
            </div>

            <div className="filter-group">
              <label className="form-label">League Type</label>
              <select
                className="form-input"
                value={leagueTypeFilter}
                onChange={(e) => setLeagueTypeFilter(e.target.value)}
              >
                <option value="all">All Leagues</option>
                <option value="super">Super Leagues</option>
                <option value="individual">Individual Leagues</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="form-label">Club</label>
              <select
                className="form-input"
                value={clubFilter}
                onChange={(e) => setClubFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="public">Public Only</option>
                <option value="club">My Clubs Only</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="form-label">Status</label>
              <select
                className="form-input"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="startingSoon">Starting Soon</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="all">All Dates</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="form-label">Minimum Level</label>
              <input
                type="number"
                className="form-input"
                placeholder="0"
                min="0"
                max="100"
                value={minLevelFilter}
                onChange={(e) => setMinLevelFilter(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label className="form-label">Maximum Level</label>
              <input
                type="number"
                className="form-input"
                placeholder="100"
                min="0"
                max="100"
                value={maxLevelFilter}
                onChange={(e) => setMaxLevelFilter(e.target.value)}
              />
            </div>

            {viewMode === 'list' && (
              <div className="filter-group">
                <label className="form-label">Distance (km)</label>
                <input
                  type="range"
                  className="form-range"
                  min="5"
                  max="50"
                  step="5"
                  value={distanceFilter}
                  onChange={(e) => setDistanceFilter(Number(e.target.value))}
                />
                <div className="range-value">{distanceFilter} km</div>
              </div>
            )}

            <div className="filter-group filter-actions">
              <button
                className="btn btn-secondary btn-block"
                onClick={() => {
                  setLeagueTypeFilter('all');
                  setDateFilter('startingSoon');
                  setSportFilter(user?.defaultSport || 'Tennis (singles)');
                  setMinLevelFilter('');
                  setMaxLevelFilter('');
                  setViewMode('map');
                  setDistanceFilter(5);
                  setClubFilter('all');
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Map */}
        {viewMode === 'map' && (
              <div className="card map-card">
            {!isLoaded ? (
                <div className="map-loading">🗺️ Loading map...</div>
            ) : loadError ? (
              <>
                <p>❌ Error loading map. Please check your Google Maps API key.</p>
                <p style={{fontSize: '12px', marginTop: '8px'}}>Error: {loadError?.message || loadError?.toString() || 'Unknown error'}</p>
              </>
            ) : (
              <>
            <div className="map-hint">
              <span>💡 Pan and zoom to filter leagues</span>
              {hasMore && !loading && (
                <span style={{marginLeft: '10px', color: '#ff9800', fontSize: '14px', fontWeight: 'bold'}}>
                  📍 Showing {leagues.length} leagues • More available nearby
                </span>
              )}
              {mapHasMoved && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm map-search-btn"
                  onClick={handleMapSearch}
                >
                  🔍 Search this area
                </button>
              )}
              {markersLoading && (
                <span style={{marginLeft: '10px', color: '#666', fontSize: '14px'}}>
                  ⏳ Clustering {leagues.length} markers...
                </span>
              )}
            </div>
            <GoogleMap
              key="main-map"
              mapContainerStyle={mapContainerStyle}
              center={mapCenterValue || DEFAULT_MAP_CENTER}
              zoom={12}
              onLoad={onMapLoad}
              onIdle={onMapIdle}
              onBoundsChanged={onBoundsChanged}
              options={mapOptions}
            >
              {/* Markers are managed via useEffect and added directly to map */}
            </GoogleMap>
            {selectedLeague && (
              <div
                className="league-popup-overlay"
                onClick={() => setSelectedLeague(null)}
                role="presentation"
              >
                <div
                  className="league-popup"
                  role="dialog"
                  aria-modal="true"
                  aria-label={`${selectedLeague.name} details`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="map-info-window">
                    <div className="info-header">
                      <h4>{selectedLeague.name}</h4>
                      <div className="info-tags">
                        <span className="badge badge-info">{selectedLeague.sport}</span>
                        {selectedLeague.isSuperLeague && (
                          <span className="badge badge-warning">Super League</span>
                        )}
                        {selectedLeague.superLeagueId && (
                          <span className="badge badge-secondary">Subleague</span>
                        )}
                      </div>
                    </div>
                    <div className="info-body">
                      <div className="info-detail">
                        <span className="detail-icon">👤</span>
                        <span>{selectedLeague.players?.length || 0} players · ⭐ {selectedLeague.interestedUsers?.filter(u => u.status !== 'approved').length || 0} interested</span>
                      </div>
                      {selectedLeague.matchFormat && (
                        <div className="info-detail">
                          <span className="detail-icon">🎾</span>
                          <span>{getMatchFormatDisplay(selectedLeague.matchFormat)}</span>
                        </div>
                      )}
                      <div className="info-detail">
                        <span className="detail-icon">🎯</span>
                        <span>Level {selectedLeague.minLevel} - {selectedLeague.maxLevel}</span>
                      </div>
                      {selectedLeague.startDate && selectedLeague.endDate && (
                        <div className="info-detail">
                          <span className="detail-icon">📅</span>
                          <span>
                            {new Date(selectedLeague.startDate).toLocaleDateString()} - {new Date(selectedLeague.endDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="info-footer">
                      <Link
                        to={selectedLeague.isSuperLeague ? `/super-leagues/${selectedLeague.leagueId}` : `/leagues/${selectedLeague.leagueId}`}
                        className="btn btn-primary btn-sm"
                      >
                        View {selectedLeague.isSuperLeague ? 'Super League' : 'League'} →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {overlappingLeagues && (
              <div
                className="league-popup-overlay"
                onClick={() => setOverlappingLeagues(null)}
                role="presentation"
              >
                <div
                  className="league-popup"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Multiple leagues at this location"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="map-info-window">
                    <div className="info-header">
                      <h4>📍 {overlappingLeagues.length} Leagues at this location</h4>
                      <button 
                        className="close-button"
                        onClick={() => setOverlappingLeagues(null)}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                    <div className="overlapping-leagues-list">
                      {overlappingLeagues.map(league => (
                        <div 
                          key={league.leagueId}
                          className="overlapping-league-item"
                          onClick={() => {
                            setOverlappingLeagues(null);
                            setSelectedLeague(league);
                          }}
                        >
                          <div className="overlapping-league-name">
                            {league.name}
                          </div>
                          <div className="overlapping-league-details">
                            <span className="badge badge-info badge-sm">{league.sport}</span>
                            {league.isSuperLeague && (
                              <span className="badge badge-warning badge-sm">Super</span>
                            )}
                            <span className="overlapping-league-level">
                              Level {league.minLevel}-{league.maxLevel}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {hasMore && (
              <div className="map-load-more">
                <span className="map-warning">
                  📍 {leagues.length} leagues loaded • Click below to load up to {25} more
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={loadMoreLeagues}
                  disabled={loadingMore}
                >
                  {loadingMore ? '⏳ Loading more...' : '📥 Load Next 25 Leagues'}
                </button>
                <div style={{fontSize: '12px', color: '#666', marginTop: '5px'}}>
                  💡 Tip: Zoom in or adjust filters to see more specific results
                </div>
              </div>
            )}
              </>
            )}
          </div>
        )}

        {/* Location Warning for List View */}
        {viewMode === 'list' && locationPermissionDenied && !userPosition && (
          <div className="card location-warning">
            <div className="location-warning-content">
              <span className="location-warning-icon">📍</span>
              <div className="location-warning-text">
                <h4>Location Access Required</h4>
                <p>
                  To view leagues near you in list view, please enable location services in your browser.
                  Alternatively, you can browse leagues using the <strong>Map view</strong> by panning to your desired location.
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setViewMode('map')}
                >
                  Switch to Map View
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Leagues Grid */}
        {viewMode === 'list' && !locationPermissionDenied && filteredLeagues.length === 0 && !loading ? (
          <div className="empty-state">
            <h3>No leagues found</h3>
            <p>
              {(leagueTypeFilter !== 'all' || dateFilter !== 'startingSoon' || minLevelFilter || maxLevelFilter)
                ? 'Try adjusting your filters or increasing the distance'
                : 'Be the first to create a league in your area!'}
            </p>
          </div>
        ) : null}

        {viewMode === 'list' && userPosition && filteredLeagues.length > 0 && (
          <div className="leagues-grid">
            {filteredLeagues.map((league) => (
              <div key={league.leagueId} className="league-card">
                <div className="league-card-header">
                  <h3>{league.name}</h3>
                  <div className="league-tags">
                    <span className="badge badge-info">{league.sport}</span>
                    {league.isSuperLeague && (
                      <span className="badge badge-warning">Super League</span>
                    )}
                    {league.superLeagueId && (
                      <span className="badge badge-secondary">Subleague</span>
                    )}
                  </div>
                </div>

                <div className="league-card-body">
                  <div className="league-detail">
                    <span className="detail-icon">👤</span>
                    <span>
                      {league.players?.length || 0} players · ⭐ {league.interestedUsers?.filter(u => u.status !== 'approved').length || 0} interested
                    </span>
                  </div>

                  {league.matchFormat && (
                    <div className="league-detail">
                      <span className="detail-icon">🎾</span>
                      <span>{getMatchFormatDisplay(league.matchFormat)}</span>
                    </div>
                  )}

                  <div className="league-detail">
                    <span className="detail-icon">🎯</span>
                    <span>
                      Level {league.minLevel} - {league.maxLevel}
                    </span>
                  </div>

                  {league.startDate && league.endDate && (
                    <div className="league-detail">
                      <span className="detail-icon">📅</span>
                      <span>
                        {new Date(league.startDate).toLocaleDateString()} - {new Date(league.endDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>

                <div className="league-card-footer">
                  <Link
                    to={league.isSuperLeague ? `/super-leagues/${league.leagueId}` : `/leagues/${league.leagueId}`}
                    className="btn btn-outline btn-block"
                  >
                    View {league.isSuperLeague ? 'Super League' : 'League'}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More Button */}
        {viewMode === 'list' && userPosition && hasMore && (
          <div className="load-more-container">
            <button
              className="btn btn-secondary"
              onClick={loadMoreLeagues}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Load More Leagues'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeagueList;

